import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Chip, Divider, FormControl, FormControlLabel, IconButton, Radio, RadioGroup, Stack, Typography } from '@mui/material'
import useCellVisualizationUrlParams from 'components/cell-visualizations/useCellVisualizationUrlParams'
import { getColumn } from 'components/cell-visualizations/arrow/arqueroHelpers'
import { DeepcellPrimaryButton } from 'components/shared'
import DeepcellAutocomplete from 'components/shared/DeepcellAutocomplete'
import useDeepcellError from 'components/shared/useDeepcellError'
import _ from 'lodash'
import { useMemo, useState } from 'react'
import { useQueryClient } from 'react-query'
import { ALL_OTHER_CELLS_ID, PinnedMorphotype, ScopeMode } from 'redux/slices'
import { useCellVisualizationsSlice } from 'redux/slices/hooks/useCellVisualizationsSlice'
import useEventManager from 'redux/slices/hooks/useEventsManager'
import { DifferentialFeaturesTaskInput, startDifferentialFeaturesTask, TaskStatus } from 'utils/api'
import { convertMorphotypeIdsToCellIdsFromDataFrame } from './morphotypeListUtils'
import { getScopeSummary } from './resultItemUtils'

// @internal
// Props for the reusable single-group scope selector sub-component
interface ScopeSelectorProps {
  scopeMode: ScopeMode
  scopeValues: string[]
  runIds: string[]
  sampleIds: string[]
  sampleTypes: string[]
  onScopeModeChange: (mode: ScopeMode) => void
  onScopeValuesChange: (values: string[]) => void
  idPrefix: string
}

// @internal
// Reusable scope selector: radio group for mode + autocomplete for values
function ScopeSelector({
  scopeMode,
  scopeValues,
  runIds,
  sampleIds,
  sampleTypes,
  onScopeModeChange,
  onScopeValuesChange,
  idPrefix,
}: ScopeSelectorProps): JSX.Element {
  const scopeOptions = useMemo(() => {
    if (scopeMode === 'RUN_ID') return runIds
    if (scopeMode === 'SAMPLE_ID') return sampleIds
    if (scopeMode === 'SAMPLE_TYPE') return sampleTypes
    return []
  }, [scopeMode, runIds, sampleIds, sampleTypes])

  const handleModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onScopeModeChange(event.target.value as ScopeMode)
  }

  const renderValueInput = () => {
    if (scopeMode === 'ALL') return null

    let label = ''
    if (scopeMode === 'RUN_ID') label = 'Included Run IDs'
    else if (scopeMode === 'SAMPLE_ID') label = 'Included Sample IDs'
    else if (scopeMode === 'SAMPLE_TYPE') label = 'Included Sample Types'

    return (
      <DeepcellAutocomplete
        multiple
        label={label}
        id={`${idPrefix}-scope-values-${scopeMode}`}
        options={scopeOptions}
        getOptionLabel={(option) => String(option)}
        filterSelectedOptions
        disableCloseOnSelect
        value={scopeValues}
        onChange={(_event, values) => {
          onScopeValuesChange(values as string[])
        }}
        renderTags={(tagValue, getTagProps) => {
          if (tagValue.length <= 2) {
            return tagValue.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index })
              return (
                <Chip
                  key={key}
                  label={String(option)}
                  {...tagProps}
                  size="small"
                />
              )
            })
          }
          const { key, ...tagProps } = getTagProps({ index: 0 })
          return (
            <Chip
              key={key}
              label={`${tagValue.length} selected`}
              {...tagProps}
              size="small"
            />
          )
        }}
      />
    )
  }

  return (
    <Stack spacing="12px">
      <FormControl component="fieldset">
        <RadioGroup
          value={scopeMode}
          onChange={handleModeChange}
          sx={{ gap: '8px' }}
        >
          <FormControlLabel value="ALL" control={<Radio size="small" />} label="All cells in session" />
          <FormControlLabel value="RUN_ID" control={<Radio size="small" />} label="Run ID" />
          <FormControlLabel value="SAMPLE_ID" control={<Radio size="small" />} label="Sample ID" />
          <FormControlLabel value="SAMPLE_TYPE" control={<Radio size="small" />} label="Sample Type" />
        </RadioGroup>
      </FormControl>
      {renderValueInput()}
    </Stack>
  )
}

// @internal
// Represents a dropdown option for each dropdown in the differential features comparison form
export type ComparisonOption = {
  morphotypeId: number
  name: string
}

/**
 * @internal
 * Returns available selection options for a dropdown, given the state of the other dropdown
 *
 * @param morphotypeIds The list of morphotype IDs selected in this dropdown (may be empty)
 * @param otherMorphotypeIds The list of morphotype IDs selected in the other dropdown (may be empty)
 * @param mergedPinnedCells The list of merged pinned cells to use to look up morphotypes ids and names
 * @param allowSameMorphotype When true, morphotypes already selected in the other dropdown are still shown
 *   (used when each group has an independent scope — same morphotype, different scope is a valid comparison)
 * */
export function getAvailableOptions(
  morphotypeIds: number[],
  otherMorphotypeIds: number[],
  pinnedCells?: PinnedMorphotype[],
  allowSameMorphotype?: boolean
): ComparisonOption[] {
  if (pinnedCells === undefined) {
    return []
  }

  const options: ComparisonOption[] = []

  /**
   * Add the "All Other Cells" option first in these cases:
   * - We haven't selected All Other Cells in the other dropdown, and we have selected some morphotypes
   * - Plus we haven't selected any morphotypes in this dropdown, or we've selected "All Other Cells" already
   */
  if (
    !otherMorphotypeIds.includes(ALL_OTHER_CELLS_ID) &&
    otherMorphotypeIds.length > 0 &&
    (morphotypeIds.length === 0 || morphotypeIds.includes(ALL_OTHER_CELLS_ID))
  ) {
    options.push({
      morphotypeId: ALL_OTHER_CELLS_ID,
      name: 'All Other Cells',
    })

    // If user has already chosen "All Other Cells", don't let them choose any other morphotypes
    if (morphotypeIds.includes(ALL_OTHER_CELLS_ID)) {
      return options
    }
  }

  const addedNames = new Set<string>()

  /** Add morphotypes that aren't in otherMorphotypeIds, unless allowSameMorphotype is set */
  pinnedCells.forEach((morphotype) => {
    if (allowSameMorphotype || !otherMorphotypeIds.includes(morphotype.id)) {
      // Add the option, if it's not already added
      if (!addedNames.has(morphotype.name)) {
        options.push({
          morphotypeId: morphotype.id,
          name: morphotype.name,
        })
        addedNames.add(morphotype.name)
      }
    }
  })

  return options
}

const CreateComparisonForm = (): JSX.Element => {
  const {
    dataFrame,
    visiblePinnedCells: pinnedCells,
    addDifferentialFeaturesComparison,
    cellVisualizations: { differentialFeaturesScope },
    setDifferentialFeaturesScope
  } = useCellVisualizationsSlice()

  const [chosenOptions1, setChosenOptions1] = useState<ComparisonOption[]>([])
  const [chosenOptions2, setChosenOptions2] = useState<ComparisonOption[]>([])

  // Per-group scope state — initialize from persisted state or defaults
  const [useSameScope, setUseSameScope] = useState<boolean>(
    differentialFeaturesScope?.useSameScope ?? true
  )
  const [scopeMode1, setScopeMode1] = useState<ScopeMode>(
    differentialFeaturesScope?.scopeMode1 || 'ALL'
  )
  const [scopeValues1, setScopeValues1] = useState<string[]>(
    differentialFeaturesScope?.scopeValues1 || []
  )
  const [scopeMode2, setScopeMode2] = useState<ScopeMode>(
    differentialFeaturesScope?.scopeMode2 || 'ALL'
  )
  const [scopeValues2, setScopeValues2] = useState<string[]>(
    differentialFeaturesScope?.scopeValues2 || []
  )
  const [isScopeExpanded, setIsScopeExpanded] = useState<boolean>(false)

  const eventsManager = useEventManager()

  const morphotypeIds1 = chosenOptions1.map((option) => option.morphotypeId)
  const morphotypeIds2 = chosenOptions2.map((option) => option.morphotypeId)

  const isScopeValid = (mode: ScopeMode, values: string[]) =>
    mode === 'ALL' || values.length > 0

  const isValidComparison =
    morphotypeIds1.length > 0 &&
    morphotypeIds2.length > 0 &&
    isScopeValid(scopeMode1, scopeValues1) &&
    (useSameScope || isScopeValid(scopeMode2, scopeValues2))

  const availableOptions1 = useMemo(
    () => getAvailableOptions(morphotypeIds1, morphotypeIds2, pinnedCells, !useSameScope),
    [morphotypeIds1, morphotypeIds2, pinnedCells, useSameScope]
  )
  const availableOptions2 = useMemo(
    () => getAvailableOptions(morphotypeIds2, morphotypeIds1, pinnedCells, !useSameScope),
    [morphotypeIds1, morphotypeIds2, pinnedCells, useSameScope]
  )

  const runIds = useMemo(() => {
    if (!dataFrame) return []
    const column = getColumn<string>(dataFrame, 'RUN_ID')
    return _.uniq(column.filter((v) => v !== undefined && v !== null && String(v).trim() !== '')).sort()
  }, [dataFrame])

  const sampleIds = useMemo(() => {
    if (!dataFrame) return []
    const column = getColumn<string>(dataFrame, 'SAMPLE_ID')
    return _.uniq(column.filter((v) => v !== undefined && v !== null && String(v).trim() !== '')).sort()
  }, [dataFrame])

  const sampleTypes = useMemo(() => {
    if (!dataFrame) return []
    const column = getColumn<string>(dataFrame, 'SAMPLE_TYPE')
    return _.uniq(column.filter((v) => v !== undefined && v !== null && String(v).trim() !== '')).sort()
  }, [dataFrame])

  const queryClient = useQueryClient()
  const { sessionId, versionId } = useCellVisualizationUrlParams()
  const { showError } = useDeepcellError()

  // Effective group-2 scope: when useSameScope, mirror group-1
  const effectiveScopeMode2 = useSameScope ? scopeMode1 : scopeMode2
  const effectiveScopeValues2 = useSameScope ? scopeValues1 : scopeValues2

  const persistScopeState = (
    sameScope: boolean,
    mode1: ScopeMode,
    vals1: string[],
    mode2: ScopeMode,
    vals2: string[]
  ) => {
    setDifferentialFeaturesScope({
      useSameScope: sameScope,
      scopeMode1: mode1,
      scopeValues1: vals1,
      scopeMode2: mode2,
      scopeValues2: vals2,
    })
  }

  const handleScopeModeChange1 = (mode: ScopeMode) => {
    setScopeMode1(mode)
    setScopeValues1([])
    if (useSameScope) {
      setScopeMode2(mode)
      setScopeValues2([])
    }
    persistScopeState(useSameScope, mode, [], useSameScope ? mode : scopeMode2, useSameScope ? [] : scopeValues2)
  }

  const handleScopeValuesChange1 = (vals: string[]) => {
    setScopeValues1(vals)
    if (useSameScope) setScopeValues2(vals)
    persistScopeState(useSameScope, scopeMode1, vals, useSameScope ? scopeMode1 : scopeMode2, useSameScope ? vals : scopeValues2)
  }

  const handleScopeModeChange2 = (mode: ScopeMode) => {
    setScopeMode2(mode)
    setScopeValues2([])
    persistScopeState(useSameScope, scopeMode1, scopeValues1, mode, [])
  }

  const handleScopeValuesChange2 = (vals: string[]) => {
    setScopeValues2(vals)
    persistScopeState(useSameScope, scopeMode1, scopeValues1, scopeMode2, vals)
  }

  const handleUseSameScopeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const sameScope = event.target.value === 'same'
    setUseSameScope(sameScope)
    if (sameScope) {
      // Mirror group-1 into group-2 when switching to "same scope"
      setScopeMode2(scopeMode1)
      setScopeValues2(scopeValues1)
      persistScopeState(true, scopeMode1, scopeValues1, scopeMode1, scopeValues1)
    } else {
      persistScopeState(false, scopeMode1, scopeValues1, scopeMode2, scopeValues2)
    }
  }

  const startComparison = async () => {
    if (!dataFrame) {
      showError('DEFAULT')
      return
    }

    const payload: DifferentialFeaturesTaskInput = {
      sessionId,
      versionId,
      cellIdList1: convertMorphotypeIdsToCellIdsFromDataFrame(
        morphotypeIds1,
        morphotypeIds2,
        dataFrame,
        pinnedCells
      ),
      cellIdList2: convertMorphotypeIdsToCellIdsFromDataFrame(
        morphotypeIds2,
        morphotypeIds1,
        dataFrame,
        pinnedCells
      ),
      scopeMode1: scopeMode1 === 'ALL' ? undefined : scopeMode1,
      scopeValues1: scopeMode1 === 'ALL' ? undefined : scopeValues1,
      scopeMode2: effectiveScopeMode2 === 'ALL' ? undefined : effectiveScopeMode2,
      scopeValues2: effectiveScopeMode2 === 'ALL' ? undefined : effectiveScopeValues2,
    }

    try {
      const result = await queryClient.fetchQuery(
        ['startDifferentialFeaturesTask', payload],
        startDifferentialFeaturesTask
      )
      const { task_id: taskId } = result.data

      eventsManager.sendMorphotypesEvent(
        morphotypeIds1.includes(ALL_OTHER_CELLS_ID) ? 'all other cells' : morphotypeIds1,
        morphotypeIds2.includes(ALL_OTHER_CELLS_ID) ? 'all other cells' : morphotypeIds2
      )

      addDifferentialFeaturesComparison({
        morphotypeIds1,
        morphotypeIds2,
        taskId,
        status: TaskStatus.PENDING,
        scopeMode1: scopeMode1 === 'ALL' ? undefined : scopeMode1,
        scopeValues1: scopeMode1 === 'ALL' ? undefined : scopeValues1,
        scopeMode2: effectiveScopeMode2 === 'ALL' ? undefined : effectiveScopeMode2,
        scopeValues2: effectiveScopeMode2 === 'ALL' ? undefined : effectiveScopeValues2,
      })

      persistScopeState(useSameScope, scopeMode1, scopeValues1, effectiveScopeMode2, effectiveScopeValues2)
    } catch (error) {
      console.error('Error starting differential features task', error)
      showError('DEFAULT')
    }
  }

  const isOptionEqualToValue = (option: ComparisonOption, value: ComparisonOption) =>
    option.morphotypeId === value.morphotypeId

  // Build header summary text for the collapsible section
  const scopeHeaderText = useMemo(() => {
    if (useSameScope) {
      return getScopeSummary({ scopeMode: scopeMode1, scopeValues: scopeValues1 }, 'Analysis Scope')
    }
    const s1 = getScopeSummary({ scopeMode: scopeMode1, scopeValues: scopeValues1 }, 'Compare')
    const s2 = getScopeSummary({ scopeMode: effectiveScopeMode2, scopeValues: effectiveScopeValues2 }, 'With')
    return `Analysis Scope: ${s1} · ${s2}`
  }, [useSameScope, scopeMode1, scopeValues1, effectiveScopeMode2, effectiveScopeValues2])

  return (
    <Stack spacing={2}>
      {/* Analysis Scope Section - Collapsible, always at top */}
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography sx={{ fontSize: '14px' }}>{scopeHeaderText}</Typography>
          <IconButton
            size="small"
            onClick={() => setIsScopeExpanded(!isScopeExpanded)}
            sx={{ p: 0.5 }}
          >
            {isScopeExpanded ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
        </Stack>

        {isScopeExpanded && (
          <FormControl component="fieldset">
            <RadioGroup
              value={useSameScope ? 'same' : 'different'}
              onChange={handleUseSameScopeChange}
              sx={{ gap: '4px' }}
            >
              <FormControlLabel
                value="same"
                control={<Radio size="small" />}
                label="Same scope for both groups"
              />
              <FormControlLabel
                value="different"
                control={<Radio size="small" />}
                label="Different scope per group"
              />
            </RadioGroup>
          </FormControl>
        )}
      </Stack>

      {/* Compare group: dropdown + inline scope selector */}
      <Stack spacing="12px">
        <DeepcellAutocomplete
          multiple
          label="Compare"
          id="cell-group-ids-1"
          options={availableOptions1}
          getOptionLabel={(option) => (option as ComparisonOption).name}
          filterSelectedOptions
          value={chosenOptions1}
          isOptionEqualToValue={isOptionEqualToValue}
          onChange={(_event, values) => {
            setChosenOptions1(values as ComparisonOption[])
          }}
        />
        {isScopeExpanded && (
          <ScopeSelector
            scopeMode={scopeMode1}
            scopeValues={scopeValues1}
            runIds={runIds}
            sampleIds={sampleIds}
            sampleTypes={sampleTypes}
            onScopeModeChange={handleScopeModeChange1}
            onScopeValuesChange={handleScopeValuesChange1}
            idPrefix="group-1"
          />
        )}
      </Stack>

      {/* With group: dropdown + inline scope selector (scope only shown for different scope mode) */}
      <Stack spacing="12px">
        <DeepcellAutocomplete
          multiple
          label="With"
          id="cell-group-ids-2"
          options={availableOptions2}
          getOptionLabel={(option) => (option as ComparisonOption).name}
          filterSelectedOptions
          value={chosenOptions2}
          isOptionEqualToValue={isOptionEqualToValue}
          onChange={(_event, values) => {
            setChosenOptions2(values as ComparisonOption[])
          }}
        />
        {isScopeExpanded && !useSameScope && (
          <ScopeSelector
            scopeMode={scopeMode2}
            scopeValues={scopeValues2}
            runIds={runIds}
            sampleIds={sampleIds}
            sampleTypes={sampleTypes}
            onScopeModeChange={handleScopeModeChange2}
            onScopeValuesChange={handleScopeValuesChange2}
            idPrefix="group-2"
          />
        )}
      </Stack>

      <Divider sx={{ my: 1 }} />

      <DeepcellPrimaryButton
        contained
        type="button"
        disabled={!isValidComparison}
        onClick={startComparison}
      >
        Compare Morphotypes
      </DeepcellPrimaryButton>
    </Stack>
  )
}

export default CreateComparisonForm
